/**
 * Detection for an edge WAF block (TR-3920).
 *
 * The edge WAF answers a blocked request with 403 ``{"error": "Request blocked",
 * "blockedBy": "waf"}``. Without this check every caller read that 403 as "no
 * data", so the case list showed "No cases available" and the timeline showed
 * as missing while the block was in place. The permission-service proxy route
 * wraps the upstream body in its own ``{"error": "<text>"}``, so the marker can
 * arrive JSON-escaped; the match allows for that.
 *
 * Limit: the edge WAF adds ``Access-Control-Allow-Origin: *`` to its block
 * responses, so a blocked GET or POST reads as a 403 here. A blocked OPTIONS
 * preflight does not: the browser reports a network error with no status,
 * which this check cannot tell apart from a dropped connection, so it does not
 * match.
 */

export const WAF_BLOCK_TITLE = 'Too Many Requests';
export const WAF_BLOCK_MESSAGE =
  'Your network sent too many requests, so they are blocked for a few minutes. Wait, then reload the page.';

const WAF_MARKER = /\\?"blockedBy\\?"\s*:\s*\\?"waf\\?"/;

export function isWafBlockBody(status: number | null | undefined, body: string | null | undefined): boolean {
  return status === 403 && typeof body === 'string' && WAF_MARKER.test(body);
}

export class WafBlockedError extends Error {
  public readonly status = 403;

  constructor(message: string, public readonly detail: string | null = null) {
    super(message);
    this.name = 'WafBlockedError';
  }
}

/** True for a {@link WafBlockedError} or any API error carrying a WAF 403 as ``status`` + ``detail``. */
export function isWafBlockError(error: unknown): boolean {
  if (error instanceof WafBlockedError) return true;
  if (!error || typeof error !== 'object') return false;
  const { status, detail } = error as { status?: unknown; detail?: unknown };
  return isWafBlockBody(typeof status === 'number' ? status : null, typeof detail === 'string' ? detail : null);
}
